/**
 * Applying custom materials to props that have been loaded from hand-authored files.
 * @module
 */
import {
  mergePropMaterialOverrides as mergePropMaterialOverridesCore,
  resolveUseExteriorPalette as resolveUseExteriorPaletteCore,
} from "building-geometry/materials/propMaterialMappingCore";
import type { FBXUserPropertiesMaterialChoice } from "building-types/materials/materialTypes";
import type { Palette } from "building-types/materials/palette";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { FullMaterialSlot, Prop, PropAttributeTable, PropMaterialOverrides } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { isDesignMaterialSrc } from "common/materials/materialSrc";
import { assertIsString, ensureDefined } from "common/utils/assertOfType";
import { createPrefixedMaterialCacheKey } from "doc/propTools";
import type { MaterialFactory } from "gfx/MaterialFactory";
import type { Object3D } from "three";
import { Material, Mesh } from "three";
import { materialFromPalette } from "./render/materialFromPalette";

interface PropForMaterials extends Pick<Prop, "src" | "materialOverrides" | "useExteriorPalette" | "isExterior"> {
  room: Pick<Room, "type"> | null;
}

interface PropForMaterials extends Pick<Prop, "src" | "materialOverrides" | "useExteriorPalette" | "isExterior"> {
  room: Pick<Room, "type"> | null;
}

/**
 * Prop material parameters with pre-resolved catalog attributes.
 * Use this interface when you don't have access to a PropAttributeTable,
 * or when you want to pre-compute the merged material overrides.
 */
export interface PropMaterialParams {
  /** Pre-merged material overrides (catalog + prop-specific) */
  mergedMaterialOverrides: PropMaterialOverrides;
  /** Whether to use exterior palette (resolved from isExterior, prop, or catalog) */
  useExteriorPalette: boolean;
  /** Room type for palette material lookup */
  roomType?: RoomType;
}

/**
 * Merges catalog material overrides with prop-specific overrides.
 * Catalog overrides are applied first, then prop overrides take precedence.
 */
export function mergePropMaterialOverrides(
  propSrc: string,
  propMaterialOverrides: PropMaterialOverrides | undefined,
  propAttributeTable: PropAttributeTable,
): PropMaterialOverrides {
  return mergePropMaterialOverridesCore(propSrc, propMaterialOverrides, propAttributeTable);
}

/**
 * Resolves useExteriorPalette from prop and catalog attributes.
 * Priority: isExterior > prop.useExteriorPalette > catalog.useExteriorPalette > false
 */
export function resolveUseExteriorPalette(
  propSrc: string,
  isExterior: boolean | undefined,
  propUseExteriorPalette: boolean | undefined,
  propAttributeTable: PropAttributeTable,
): boolean {
  return resolveUseExteriorPaletteCore(propSrc, isExterior, propUseExteriorPalette, propAttributeTable);
}

/**
 * Resolves prop material parameters from a prop and optional attribute table.
 * Use this to pre-compute parameters for propSetMaterialResolved.
 */
export function resolvePropMaterialParams(
  prop: PropForMaterials,
  propAttributeTable?: PropAttributeTable,
): PropMaterialParams {
  const propAttributes = propAttributeTable?.[prop.src];
  const catalogMaterialOverrides = propAttributes?.materialOverrides;

  return {
    mergedMaterialOverrides: {
      ...catalogMaterialOverrides,
      ...prop.materialOverrides,
    },
    useExteriorPalette: (prop.isExterior || prop.useExteriorPalette) ?? propAttributes?.useExteriorPalette ?? false,
    roomType: prop.room?.type,
  };
}

/**
 * Apply materials to a prop's submeshes using pre-resolved material parameters.
 * Use this when you don't have access to a PropAttributeTable.
 */
export async function propSetMaterialResolved(
  params: PropMaterialParams,
  materialFactory: MaterialFactory,
  object: Object3D,
  exteriorPalette: Palette | null,
  interiorPalette: Palette | null,
  materialMapping?: (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice,
): Promise<Material[] | undefined> {
  if (!object) {
    return undefined;
  }

  const propSubmeshes = collectPropSubmeshesAndMaterialsResolved(
    params,
    object,
    exteriorPalette,
    interiorPalette,
    materialMapping,
  );

  const materialPromises: Promise<any>[] = [];

  for (const propSubmesh of propSubmeshes) {
    switch (propSubmesh.materialType) {
      case PropSubmeshMaterialType.DEFAULT:
        materialPromises.push(
          materialFactory
            .getDesignMaterial({
              ...DesignMaterialPath.Color.C,
              colorId: DMC.SystemColors.Light_Cream,
            })
            .then((mat: Material | undefined) => {
              propSubmesh.obj.material = ensureDefined(mat);
            }),
        );
        break;
      case PropSubmeshMaterialType.PALETTE_SLOT: {
        const palette = propSubmesh.useExteriorPalette ? exteriorPalette : interiorPalette;
        materialPromises.push(
          materialFromPalette(materialFactory, palette, propSubmesh.paletteSlotId, params.roomType).then(
            (mat: Material | undefined) => (propSubmesh.obj.material = ensureDefined(mat)),
          ),
        );
        break;
      }
      case PropSubmeshMaterialType.MATERIAL_SRC:
        materialPromises.push(
          materialFactory
            .getDesignMaterial(propSubmesh.materialSrc)
            .then((mat: Material | undefined) => (propSubmesh.obj.material = ensureDefined(mat))),
        );
        break;
    }
  }

  return Promise.all(materialPromises);
}

/**
 * Apply materials to a prop's submeshes, resolving catalog attributes from the table.
 * For cases without a PropAttributeTable, use propSetMaterialResolved instead.
 */
export async function propSetMaterial(
  prop: PropForMaterials,
  materialFactory: MaterialFactory,
  object: Object3D,
  exteriorPalette: Palette | null,
  interiorPalette: Palette | null,
  materialMapping?: (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice,
  propAttributeTable?: PropAttributeTable,
): Promise<any[] | undefined> {
  const params = resolvePropMaterialParams(prop, propAttributeTable);
  return propSetMaterialResolved(params, materialFactory, object, exteriorPalette, interiorPalette, materialMapping);
}

export enum PropSubmeshMaterialType {
  DEFAULT,
  PALETTE_SLOT,
  MATERIAL_SRC,
}

export interface PropSubmeshBase {
  obj: Mesh;
}

export interface PropSubmeshDefault extends PropSubmeshBase {
  materialType: PropSubmeshMaterialType.DEFAULT;
}

export interface PropSubmeshPaletteSlot extends PropSubmeshBase {
  materialType: PropSubmeshMaterialType.PALETTE_SLOT;
  paletteSlotId: PaletteMaterialSlot;
  useExteriorPalette: boolean;
}

export interface PropSubmeshMaterialSrc extends PropSubmeshBase {
  materialType: PropSubmeshMaterialType.MATERIAL_SRC;
  materialSrc: MaterialSrc;
}

export type PropSubmeshAndMaterial = PropSubmeshDefault | PropSubmeshPaletteSlot | PropSubmeshMaterialSrc;

// Remove characters that are not valid in HTML attribute names.
export function sanitizeMaterialName(name?: string): string {
  return name?.replace(/[^a-zA-Z0-9\-_]/g, "") || "";
}

/**
 * Collect prop submeshes and determine their materials using pre-resolved parameters.
 * Use this when you don't have access to a PropAttributeTable.
 */
export function collectPropSubmeshesAndMaterialsResolved(
  params: PropMaterialParams,
  object: Object3D,
  exteriorPalette: Palette | null,
  interiorPalette: Palette | null,
  materialMapping?: (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice,
): PropSubmeshAndMaterial[] {
  if (!object) {
    return [];
  }

  const { mergedMaterialOverrides, useExteriorPalette: defaultUseExteriorPalette } = params;
  const collectedObjs: PropSubmeshAndMaterial[] = [];

  object.traverse((obj) => {
    if (obj instanceof Mesh && obj.material instanceof Material) {
      let materialId: FBXUserPropertiesMaterialChoice | undefined = undefined;
      let useExteriorPalette = defaultUseExteriorPalette;
      const higharcMaterial = obj.material.userData.fromFBX?.userProperties?.higharcMaterial?.value;
      let fullMaterialSlot: FullMaterialSlot | undefined;
      if (higharcMaterial) {
        fullMaterialSlot = applyPropMaterialOverrides(mergedMaterialOverrides, higharcMaterial);
      } else {
        const materialName = sanitizeMaterialName(obj.material.name);
        // We don't allow "materialName" to be used directly as "materialId", but only allow materials coming from prop material overrides in this case.
        fullMaterialSlot = mergedMaterialOverrides[materialName];
      }
      if (fullMaterialSlot) {
        materialId = fullMaterialSlot.paletteMaterialSlot;
        useExteriorPalette = fullMaterialSlot.useExteriorPalette ?? useExteriorPalette;
      } else {
        materialId = higharcMaterial;
      }
      if (!interiorPalette) {
        useExteriorPalette = true;
      }
      if (!exteriorPalette) {
        useExteriorPalette = false;
      }

      if (materialMapping && materialId) {
        materialId = materialMapping(materialId);
      }

      let materialSrc: MaterialSrc | undefined;

      if (isDesignMaterialSrc(materialId as MaterialSrc)) {
        materialSrc = materialId as MaterialSrc;
      }

      if (materialSrc) {
        collectedObjs.push({
          obj,
          materialType: PropSubmeshMaterialType.MATERIAL_SRC,
          materialSrc,
        });
        return;
      }
      const palette = useExteriorPalette ? exteriorPalette : interiorPalette;

      // if there is a palette material id, but no palette then we use the default material
      if (!palette) {
        collectedObjs.push({ obj, materialType: PropSubmeshMaterialType.DEFAULT });
        return;
      }

      const paletteSlotId = materialId
        ? (materialId as PaletteMaterialSlot)
        : mergedMaterialOverrides["propDefault0"]?.paletteMaterialSlot ?? "propDefault0";
      collectedObjs.push({
        obj,
        materialType: PropSubmeshMaterialType.PALETTE_SLOT,
        paletteSlotId,
        useExteriorPalette,
      });
    }
  });

  return collectedObjs;
}

/**
 * Collect prop submeshes and determine their materials, resolving catalog attributes from the table.
 * For cases without a PropAttributeTable, use collectPropSubmeshesAndMaterialsResolved instead.
 */
export function collectPropSubmeshesAndMaterials(
  prop: PropForMaterials,
  object: Object3D,
  exteriorPalette: Palette | null,
  interiorPalette: Palette | null,
  materialMapping?: (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice,
  propAttributeTable?: PropAttributeTable,
): PropSubmeshAndMaterial[] {
  const params = resolvePropMaterialParams(prop, propAttributeTable);
  return collectPropSubmeshesAndMaterialsResolved(params, object, exteriorPalette, interiorPalette, materialMapping);
}

export function propMaterialIdToMaterialOverrideKey(materialId: FBXUserPropertiesMaterialChoice): string {
  if (isDesignMaterialSrc(materialId as MaterialSrc)) {
    // Material keys are stored as prefixed MaterialSrc cache keys in the prop materialOverrides table
    return createPrefixedMaterialCacheKey(materialId as MaterialSrc);
  }
  assertIsString(materialId);
  return materialId;
}

function applyPropMaterialOverrides(
  mergedOverrides: PropMaterialOverrides,
  materialId: FBXUserPropertiesMaterialChoice,
): FullMaterialSlot | undefined {
  const materialOverrideKey = propMaterialIdToMaterialOverrideKey(materialId);
  if (materialOverrideKey) {
    return mergedOverrides[materialOverrideKey];
  }
  return undefined;
}
